import { useEffect, useMemo, useState } from 'react';
import Card from 'components/card';
import ReactApexChart from 'react-apexcharts';
import { IoInformationCircleOutline } from 'react-icons/io5';
import { getSupplierPurchasesByYear } from 'interfaces/supplier';

type ProductRow = {
    productId: string;
    name: string;
    ref: string;
    unity: string;
    quantity: number;
    totalHT: number;
};

type YearStats = {
    year: number;
    monthly: { totalHT: number; quantity: number }[];
    totalHT: number;
    totalQuantity: number;
    products: ProductRow[];
};

const MOIS = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

const formatMoney = (v: number) => {
    if (!Number.isFinite(v)) return '—';
    return Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(v);
};

const formatNumber = (v: number) => {
    if (!Number.isFinite(v)) return '—';
    return Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(v);
};

export default function SupplierPurchases(props: { id: string }) {
    const { id } = props;
    const currentYear = new Date().getFullYear();
    const [year, setYear] = useState(currentYear);
    const [metric, setMetric] = useState<'amount' | 'quantity'>('amount');
    const [stats, setStats] = useState<YearStats | null>(null);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [visibleItems, setVisibleItems] = useState(8);

    const fetchStats = async () => {
        if (!id) return;
        setLoading(true);
        const res = await getSupplierPurchasesByYear(id, year);
        if (res) setStats(res);
        setLoading(false);
    };

    useEffect(() => {
        fetchStats();
        setVisibleItems(8);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, year]);

    const yearOptions = useMemo(() => {
        const years = [];
        for (let y = currentYear + 1; y >= currentYear - 3; y--) years.push(y);
        return years;
    }, [currentYear]);

    const products = useMemo(() => {
        const list = stats?.products || [];
        if (!search.trim()) return list;
        const q = search.trim().toLowerCase();
        return list.filter(
            (p) => p.name?.toLowerCase().includes(q) || p.ref?.toLowerCase().includes(q)
        );
    }, [stats, search]);

    const monthlySeries = [
        {
            name: metric === 'amount' ? 'Achats HT' : 'Quantité',
            data: (stats?.monthly || Array(12).fill({ totalHT: 0, quantity: 0 })).map((m) =>
                metric === 'amount' ? Number(m.totalHT.toFixed(2)) : Number(m.quantity.toFixed(2))
            ),
        },
    ];

    const monthlyOptions: any = {
        chart: { type: 'bar', toolbar: { show: false } },
        colors: ['#0260CB'],
        plotOptions: {
            bar: { horizontal: false, columnWidth: '55%', borderRadius: 4, borderRadiusApplication: 'end' },
        },
        dataLabels: { enabled: false },
        xaxis: {
            categories: MOIS,
            labels: { style: { colors: '#A3AED0', fontSize: '10px' } },
        },
        yaxis: {
            labels: {
                style: { colors: '#A3AED0', fontSize: '10px' },
                formatter: (v: number) => (metric === 'amount' ? `${Math.round(v)}€` : `${v}`),
            },
        },
        grid: { show: false },
        tooltip: {
            theme: 'dark',
            y: { formatter: (v: number) => (metric === 'amount' ? formatMoney(v) : formatNumber(v)) },
        },
    };

    const top10 = products.slice(0, 10);
    const productSeries = [
        {
            name: metric === 'amount' ? 'Achats HT' : 'Quantité',
            data: top10.map((p) => (metric === 'amount' ? p.totalHT : p.quantity)),
        },
    ];
    const MAX_LABEL = 22;
    const truncateLabel = (s: string) => (s && s.length > MAX_LABEL ? s.slice(0, MAX_LABEL - 1) + '…' : s || '');
    const productOptions: any = {
        chart: { type: 'bar', toolbar: { show: false } },
        colors: ['#0260CB'],
        plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: '60%' } },
        dataLabels: { enabled: false },
        xaxis: {
            categories: top10.map((p) => truncateLabel(p.name)),
            labels: {
                style: { colors: '#A3AED0', fontSize: '10px' },
                formatter: (v: number) => (metric === 'amount' ? `${Math.round(v)}€` : `${v}`),
            },
        },
        yaxis: { labels: { style: { colors: '#A3AED0', fontSize: '11px' }, maxWidth: 160 } },
        tooltip: {
            theme: 'dark',
            x: { formatter: (_: any, opts: any) => top10[opts.dataPointIndex]?.name || '—' },
            y: { formatter: (v: number) => (metric === 'amount' ? formatMoney(v) : formatNumber(v)) },
        },
        grid: { show: false },
    };

    return (
        <div className="col-span-12 flex flex-col gap-3">
            <Card extra={'w-full px-6 pb-6 border overflow-hidden'}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center space-x-2">
                        <h4 className="mt-5 text-lg font-medium text-navy-700 dark:text-white">
                            Achats mensuels
                        </h4>
                        <div className="group relative mt-5">
                            <IoInformationCircleOutline className="h-4 w-4 cursor-pointer text-gray-500" />
                            <div className="pointer-events-none absolute left-0 z-10 mt-2 w-64 max-w-[calc(100vw-3rem)] rounded-md bg-gray-700 p-2 text-xs text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
                                Les achats sont attribués à chaque mois en fonction de la date de commande. Les commandes en litige sont exclues.
                            </div>
                        </div>
                    </div>
                    <div className="mt-5 flex items-center gap-3">
                        <div className="flex gap-3 text-sm">
                            <button
                                onClick={() => setMetric('amount')}
                                className={`cursor-pointer ${metric === 'amount' ? 'font-bold text-toola-500 underline' : 'text-gray-500'}`}
                            >
                                Montant
                            </button>
                            <button
                                onClick={() => setMetric('quantity')}
                                className={`cursor-pointer ${metric === 'quantity' ? 'font-bold text-toola-500 underline' : 'text-gray-500'}`}
                            >
                                Quantité
                            </button>
                        </div>
                        <select
                            className="rounded-md border bg-white p-1 text-sm dark:bg-navy-800 dark:text-white"
                            value={year}
                            onChange={(e) => setYear(Number(e.target.value))}
                        >
                            {yearOptions.map((y) => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {stats && (
                    <p className="mt-2 text-sm text-gray-600">
                        Total {year} :{' '}
                        <span className="font-bold text-navy-700 dark:text-white">{formatMoney(stats.totalHT)} HT</span>
                        {' · '}
                        <span className="font-bold text-navy-700 dark:text-white">{formatNumber(stats.totalQuantity)} unités</span>
                    </p>
                )}

                <div className="mt-2">
                    {loading ? (
                        <div className="flex h-[260px] items-center justify-center">
                            <p className="text-gray-400 dark:text-white">Chargement...</p>
                        </div>
                    ) : !stats || (stats.totalHT === 0 && stats.totalQuantity === 0) ? (
                        <div className="flex h-[260px] items-center justify-center">
                            <p className="text-gray-400 dark:text-white">Aucun achat sur {year}</p>
                        </div>
                    ) : (
                        <ReactApexChart options={monthlyOptions} series={monthlySeries} type="bar" height={260} />
                    )}
                </div>
            </Card>

            <Card extra={'w-full px-6 pb-6 border'}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <h4 className="mt-5 text-lg font-medium text-navy-700 dark:text-white">
                        Achats par produit
                    </h4>
                    <input
                        type="text"
                        placeholder="Rechercher un produit..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="mt-5 w-48 rounded-md border border-gray-200 px-2 py-1 text-sm outline-none focus:border-blue-400 dark:bg-navy-800 dark:text-white"
                    />
                </div>

                {loading ? (
                    <div className="flex h-[200px] items-center justify-center">
                        <p className="text-gray-400 dark:text-white">Chargement...</p>
                    </div>
                ) : products.length === 0 ? (
                    <div className="flex h-[200px] items-center justify-center">
                        <p className="text-gray-400 dark:text-white">
                            {search ? 'Aucun produit ne correspond à la recherche' : `Aucun achat sur ${year}`}
                        </p>
                    </div>
                ) : (
                    <>
                        {top10.length > 0 && (
                            <div className="mt-2">
                                <ReactApexChart
                                    options={productOptions}
                                    series={productSeries}
                                    type="bar"
                                    height={Math.max(220, top10.length * 34)}
                                />
                            </div>
                        )}

                        <div className="mt-4 overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="!border-px !border-gray-400">
                                        <th className="border-b border-gray-200 pt-4 pb-2 pr-4 text-start dark:border-white/30">
                                            <p className="text-xs font-bold text-gray-600 dark:text-white">Référence</p>
                                        </th>
                                        <th className="border-b border-gray-200 pt-4 pb-2 pr-4 text-start dark:border-white/30">
                                            <p className="text-xs font-bold text-gray-600 dark:text-white">Produit</p>
                                        </th>
                                        <th className="border-b border-gray-200 pt-4 pb-2 pr-4 text-start dark:border-white/30">
                                            <p className="text-xs font-bold text-gray-600 dark:text-white">Quantité</p>
                                        </th>
                                        <th className="border-b border-gray-200 pt-4 pb-2 pr-4 text-start dark:border-white/30">
                                            <p className="text-xs font-bold text-gray-600 dark:text-white">Total HT</p>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {products.slice(0, visibleItems).map((p) => (
                                        <tr key={p.productId}>
                                            <td className="min-w-[90px] border-white/0 py-2 px-2">
                                                <p className="text-sm font-medium text-navy-700 dark:text-white">{p.ref || '-'}</p>
                                            </td>
                                            <td className="min-w-[90px] border-white/0 py-2 px-2">
                                                <p className="text-sm font-medium text-navy-700 dark:text-white">{p.name}</p>
                                            </td>
                                            <td className="min-w-[90px] border-white/0 py-2 px-2">
                                                <p className="text-sm font-medium text-navy-700 dark:text-white">
                                                    {formatNumber(p.quantity)} {p.unity || ''}
                                                </p>
                                            </td>
                                            <td className="min-w-[90px] border-white/0 py-2 px-2">
                                                <p className="text-sm font-bold text-navy-700 dark:text-white">{formatMoney(p.totalHT)}</p>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {products.length > visibleItems && (
                            <div className="mt-4 flex justify-center">
                                <button
                                    onClick={() => setVisibleItems(visibleItems + 10)}
                                    className="rounded-lg border border-toola-500 px-4 py-2 text-sm font-medium text-toola-500 transition-colors hover:bg-toola-50"
                                >
                                    Voir plus ({products.length - visibleItems} restants)
                                </button>
                            </div>
                        )}
                    </>
                )}
            </Card>
        </div>
    );
}
